import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../utils/api";
import Navbar from "../components/Navbar";
import * as Icons from "lucide-react";
import { Lock, BookOpen } from "lucide-react";
import { TOOL_DOCS_BY_ID } from "../docs/toolDocs";

const TIER_BADGE = { starter: "bg-sage-100 text-sage-700", professional: "bg-amber-100 text-amber-700", enterprise: "bg-blue-100 text-blue-700" };
const CATEGORY_COLORS = { Diagnose: "border-blue-200 bg-blue-50/40", Design: "border-amber-200 bg-amber-50/40", Implement: "border-sage-200 bg-sage-50/40", Sustain: "border-purple-200 bg-purple-50/40" };

export default function CataloguePage() {
  const [tools, setTools] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tools").then(r => { setTools(r.data.tools); setPkg(r.data.package); }).finally(() => setLoading(false));
  }, []);

  const byCategory = tools.reduce((acc, t) => ({ ...acc, [t.category]: [...(acc[t.category] || []), t] }), {});

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Toolbox</p>
          <h1 className="font-heading text-2xl font-semibold text-sage-900">Lean Tools Catalogue</h1>
          <p className="text-sage-500 text-sm mt-1">Every tool is a guided wizard. {pkg ? `You have ${pkg.tools_enabled.length} of ${tools.length} unlocked.` : "Buy a plan to start unlocking."}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-sage-500">Loading…</div>
        ) : (
          <div className="space-y-6" data-testid="tool-catalogue">
            {Object.entries(byCategory).map(([cat, list]) => (
              <section key={cat}>
                <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-sage-600 mb-3">{cat}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map(t => {
                    const Icon = Icons[t.icon] || Icons.Wrench;
                    const unlocked = t.unlocked;
                    return (
                      <div key={t.id} className={`relative rounded-md border ${CATEGORY_COLORS[cat] || "border-sage-200"} p-5 ${unlocked ? "" : "opacity-70"}`} data-testid={`tool-card-${t.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${unlocked ? "bg-sage-600 text-white" : "bg-sage-100 text-sage-400"}`}>
                            {unlocked ? <Icon className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${TIER_BADGE[t.tier] || "bg-sage-100 text-sage-700"}`}>{t.tier}</span>
                        </div>
                        <h3 className="font-heading text-base font-semibold text-sage-900">{t.name}</h3>
                        <p className="text-xs text-sage-600 mt-1 leading-relaxed min-h-[50px]">{t.desc}</p>
                        <div className="flex items-center justify-between mt-3 gap-2">
                          {unlocked ? (
                            <Link to="/" data-testid={`use-tool-${t.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-sage-700 hover:text-sage-900">Use in a company →</Link>
                          ) : (
                            <Link to="/pricing" data-testid={`unlock-tool-${t.id}`} className="bg-sage-600 text-white text-xs font-semibold rounded-md px-3 py-1.5 hover:bg-sage-700">Unlock</Link>
                          )}
                          {TOOL_DOCS_BY_ID[t.id] && (
                            <Link
                              to={`/docs/${t.id}`}
                              data-testid={`docs-link-${t.id}`}
                              className="inline-flex items-center gap-1 text-xs text-sage-500 hover:text-sage-800 border border-sage-200 hover:border-sage-400 rounded-md px-2 py-1 transition-colors"
                              title="Open docs"
                            >
                              <BookOpen className="w-3 h-3" /> Docs
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
